import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react";
import { useState } from "react";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";
import { Trash2, Plus, Settings2 } from "lucide-react";

const PALETTE = ["#A8785A", "#D4A0B4", "#C39B7B", "#8C7562", "#C48A9F", "#E8DCCB", "#57473B"];

function ChartView({ node, updateAttributes, deleteNode, editor }) {
  const chart = node.attrs.chart || { title: "Chart", type: "bar", data: [] };
  const [open, setOpen] = useState(chart.data.length === 0);
  const editable = editor.isEditable;
  const set = (patch) => updateAttributes({ chart: { ...chart, ...patch } });
  const setRow = (i, k, v) => set({ data: chart.data.map((r, j) => (j === i ? { ...r, [k]: k === "value" ? Number(v) || 0 : v } : r)) });
  const data = chart.data.filter((d) => d.label);

  return (
    <NodeViewWrapper className="my-4 rounded-xl border border-[#E8DCCB] bg-[#FFFDF7] p-4" data-testid="notes-chart" contentEditable={false}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <p className="f-display text-lg tracking-tight">{chart.title || "Chart"}</p>
        {editable && (
          <div className="flex items-center gap-1.5">
            <button type="button" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1.5 text-[11px] font-semibold border border-[#E8DCCB] rounded-full px-3 py-1.5 text-[#8C7562] hover:text-[#3A2E25] hover:border-[#A8785A] transition-colors" data-testid="chart-edit-btn"><Settings2 size={12} /> {open ? "Done" : "Edit data"}</button>
            <button type="button" onClick={deleteNode} className="inline-flex items-center gap-1.5 text-[11px] font-semibold border border-[#E8DCCB] rounded-full px-3 py-1.5 text-[#8C7562] hover:text-[#FFFDF7] hover:bg-[#A8785A] hover:border-[#A8785A] transition-colors" data-testid="chart-delete-btn"><Trash2 size={12} /> Remove chart</button>
          </div>
        )}
      </div>
      <div className="h-56">
        {data.length === 0 ? (
          <div className="h-full flex items-center justify-center text-xs text-[#B9A896]">Add a few rows below to draw the chart.</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {chart.type === "pie" ? (
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="label" outerRadius={90} label={({ name }) => name}>
                  {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            ) : chart.type === "line" ? (
              <LineChart data={data}>
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#8C7562" }} /><YAxis tick={{ fontSize: 11, fill: "#8C7562" }} /><Tooltip />
                <Line type="monotone" dataKey="value" stroke="#A8785A" strokeWidth={2} dot={{ fill: "#D4A0B4" }} />
              </LineChart>
            ) : (
              <BarChart data={data}>
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#8C7562" }} /><YAxis tick={{ fontSize: 11, fill: "#8C7562" }} /><Tooltip />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>{data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}</Bar>
              </BarChart>
            )}
          </ResponsiveContainer>
        )}
      </div>
      {editable && open && (
        <div className="mt-4 pt-4 border-t border-[#E8DCCB] flex flex-col gap-2" data-testid="chart-editor">
          <div className="flex flex-col sm:flex-row gap-2">
            <input className="field !py-2 !text-sm flex-1" value={chart.title} onChange={(e) => set({ title: e.target.value })} placeholder="Chart title" data-testid="chart-title-input" />
            <select className="field !py-2 !text-sm sm:w-36" value={chart.type} onChange={(e) => set({ type: e.target.value })} data-testid="chart-type-select">
              <option value="bar">Bar</option><option value="pie">Pie</option><option value="line">Line</option>
            </select>
          </div>
          {chart.data.map((r, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input className="field !py-2 !text-sm flex-1" value={r.label} onChange={(e) => setRow(i, "label", e.target.value)} placeholder="Label" data-testid={`chart-row-label-${i}`} />
              <input className="field !py-2 !text-sm w-28" type="number" value={r.value} onChange={(e) => setRow(i, "value", e.target.value)} placeholder="Value" data-testid={`chart-row-value-${i}`} />
              <button type="button" onClick={() => set({ data: chart.data.filter((_, j) => j !== i) })} className="text-[#B9A896] hover:text-[#A8785A]" aria-label="Remove row"><Trash2 size={14} /></button>
            </div>
          ))}
          <button type="button" onClick={() => set({ data: [...chart.data, { label: "", value: 0 }] })} className="btn-ghost !py-2 !px-4 !text-[11px] self-start" data-testid="chart-add-row-btn"><Plus size={12} /> Add row</button>
        </div>
      )}
    </NodeViewWrapper>
  );
}

export const ChartNode = Node.create({
  name: "chart",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      chart: {
        default: { title: "Chart", type: "bar", data: [] },
        parseHTML: (el) => { try { return JSON.parse(el.getAttribute("data-chart") || "{}"); } catch { return { title: "Chart", type: "bar", data: [] }; } },
        renderHTML: (attrs) => ({ "data-chart": JSON.stringify(attrs.chart) }),
      },
    };
  },
  parseHTML() { return [{ tag: 'div[data-type="chart"]' }]; },
  renderHTML({ HTMLAttributes }) { return ["div", mergeAttributes(HTMLAttributes, { "data-type": "chart" })]; },
  addNodeView() { return ReactNodeViewRenderer(ChartView); },
});

export const VideoNode = Node.create({
  name: "video",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes() { return { src: { default: null } }; },
  parseHTML() { return [{ tag: "video" }]; },
  renderHTML({ HTMLAttributes }) {
    return ["video", mergeAttributes(HTMLAttributes, { controls: "true", playsinline: "true", preload: "metadata", class: "notes-video" })];
  },
});
